import React, { useState, useEffect, useCallback } from 'react'
import VendorMapping from './VendorMapping';
import { apiFetch } from './api';

export default function RouteMapping({ company, canEdit, canEditSupervisors, supervisorId }) {
  const [routes, setRoutes] = useState([])
  const [supervisors, setSupervisors] = useState([])
  const [modalSupervisor, setModalSupervisor] = useState('')
  const [newName, setNewName] = useState('')
  const [newRouteId, setNewRouteId] = useState('')
  const [editId, setEditId] = useState(null)
  const [editName, setEditName] = useState('')
  const [editRouteId, setEditRouteId] = useState('')
  const [loading, setLoading] = useState(false)
  const [routesLoading, setRoutesLoading] = useState(false)
  const [vendorModalRoute, setVendorModalRoute] = useState(null)

  // selectedSupervisor is the currently selected supervisor in the modal
  const selectedSupervisor = modalSupervisor;

  useEffect(() => {
    if (supervisorId) {
      // Only fetch this supervisor
      apiFetch(`/api/supervisors/${supervisorId}`, { credentials: 'include' })
        .then(r => r.ok ? r.json() : null)
        .then(sup => {
          if (sup) {
            setSupervisors([sup]);
            setModalSupervisor(String(sup.id));
          } else {
            setSupervisors([]);
            setModalSupervisor('');
          }
        })
    } else {
      apiFetch(`/api/supervisors?company=${company}`, { credentials: 'include' })
        .then(r => r.ok ? r.json() : [])
        .then(list => {
          setSupervisors(list)
          if (modalSupervisor && list.every(s => String(s.id) !== String(modalSupervisor))) {
            setModalSupervisor('')
          }
        })
    }
  }, [company, modalSupervisor, supervisorId])

  const loadRoutes = useCallback(async (supId) => {
    if (!supId) {
      setRoutes([])
      return
    }
    setRoutesLoading(true)
    const res = await apiFetch(`/api/routes?supervisorId=${supId}`, { credentials: 'include' })
    const data = res.ok ? await res.json() : []
    setRoutes(data)
    setRoutesLoading(false)
  }, [])

  useEffect(() => {
    if (modalSupervisor) {
      loadRoutes(modalSupervisor)
    }
  }, [modalSupervisor, loadRoutes])

  const handleAdd = async e => {
    e.preventDefault()
    if (!newName.trim() || !newRouteId.trim() || !selectedSupervisor) return
    setLoading(true)
    const res = await apiFetch(`/api/routes?supervisorId=${selectedSupervisor}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ name: newName, routeId: newRouteId })
    })
    if (res.ok) {
      setNewName('')
      setNewRouteId('')
      await loadRoutes(selectedSupervisor)
    }
    setLoading(false)
  }

  const startEdit = route => {
    setEditId(route.id)
    setEditName(route.name)
    setEditRouteId(route.routeId || route.id || '')
  }

  const handleEdit = async e => {
    e.preventDefault()
    if (!selectedSupervisor) return
    setLoading(true)
    const res = await apiFetch(`/api/routes/${editId}?supervisorId=${selectedSupervisor}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ name: editName, routeId: editRouteId })
    })
    if (res.ok) {
      setEditId(null)
      setEditName('')
      setEditRouteId('')
      await loadRoutes(selectedSupervisor)
    }
    setLoading(false)
  }

  const handleDelete = async id => {
    setLoading(true)
    const res = await apiFetch(`/api/routes/${id}`, {
      method: 'DELETE',
      credentials: 'include'
    })
    if (res.ok) {
      if (editId === id) { setEditId(null); setEditName('') }
      await loadRoutes(selectedSupervisor)
    }
    setLoading(false)
  }

  const selected = supervisors.find(s => String(s.id) === String(modalSupervisor))

  return (
    <div style={{height:'100%',display:'flex',gap:16}}>
      <div style={{flex:1,background:'#f7f9fc',borderRadius:16,padding:'28px 24px 24px 24px',border:'1px solid #e8ecf3',overflowY:'auto',boxShadow:'0 2px 8px rgba(34,50,80,0.04)',minWidth:340,display:'flex',flexDirection:'column',justifyContent:'flex-start',height:'100%'}}>
        <div style={{fontWeight:900,marginBottom:22,fontSize:22,letterSpacing:0.3,color:'#1d2a3b',textAlign:'center'}}>Route and Vendor Mapping</div>
        <div style={{display:'flex',flexDirection:'column',gap:16}}>
          {supervisors.map(s => (
            <button
              key={s.id}
              type="button"
              onClick={() => {
                setModalSupervisor(String(s.id));
                setEditId(null); setEditName(''); setEditRouteId('');
              }}
              style={{
                width:'100%',
                textAlign:'left',
                background:'#fff',
                color:'#1d2a3b',
                fontWeight:600,
                border:'1.5px solid #2563eb',
                borderRadius:12,
                padding:'18px 22px',
                fontSize:17,
                cursor:'pointer',
                outline:'none',
                transition:'all 0.2s',
                display:'flex',alignItems:'center',justifyContent:'space-between',gap:12,
                boxShadow:'0 2px 8px #2563eb11',
                letterSpacing:0.2
              }}
              onMouseOver={e => e.currentTarget.style.background = '#f0f6ff'}
              onMouseOut={e => e.currentTarget.style.background = '#fff'}
            >
              <span style={{display:'flex',alignItems:'center',gap:10}}>
                <span style={{background:'#2563eb',color:'#fff',borderRadius:'50%',width:32,height:32,display:'flex',alignItems:'center',justifyContent:'center',fontWeight:700,fontSize:16,boxShadow:'0 1px 4px #2563eb22'}}>{s.name.charAt(0)}</span>
                <span>{s.name}</span>
              </span>
              <span style={{fontSize:22,marginLeft:8,color:'#2563eb',fontWeight:700}}>→</span>
            </button>
          ))}
          {supervisors.length === 0 && <div style={{color:'#888',padding:'8px 0'}}>No supervisors yet.</div>}
        </div>
      </div>
      {/* Modal for supervisor details and routes */}
      {modalSupervisor && selected && (
        <div style={{position:'fixed',top:0,left:0,width:'100vw',height:'100vh',background:'rgba(16,23,42,0.72)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',backdropFilter:'blur(2px)'}}>
          <div style={{background:'#fff',borderRadius:14,padding:32,minWidth:420,maxWidth:600,boxShadow:'0 16px 64px 0 rgba(15,23,42,0.45)',position:'relative'}}>
            <button onClick={()=>{setModalSupervisor(''); setEditId(null); setEditName(''); setEditRouteId('')}} style={{position:'absolute',top:18,right:18,fontSize:22,background:'none',border:'none',cursor:'pointer',color:'#64748b'}}>&times;</button>
            <div style={{fontWeight:800,fontSize:22,marginBottom:18}}>Routes for {selected.name}</div>
            {canEdit && (
              <form onSubmit={editId ? handleEdit : handleAdd} style={{display:'flex',gap:8,marginBottom:18}}>
                <input
                  placeholder="Route name"
                  value={editId ? editName : newName}
                  onChange={e => editId ? setEditName(e.target.value) : setNewName(e.target.value)}
                  style={{flex:2,padding:8,borderRadius:6,border:'1px solid #e6eaf0'}}
                  required
                  disabled={loading}
                />
                <input
                  placeholder="Route ID"
                  value={editId ? editRouteId : newRouteId}
                  onChange={e => editId ? setEditRouteId(e.target.value) : setNewRouteId(e.target.value)}
                  style={{flex:1,padding:8,borderRadius:6,border:'1px solid #e6eaf0'}}
                  required
                  disabled={loading}
                />
                <button className="btn primary" type="submit" disabled={loading}>{editId ? 'Save' : 'Add route'}</button>
                {editId && <button className="btn secondary" type="button" onClick={()=>{setEditId(null); setEditName(''); setEditRouteId('')}} disabled={loading}>Cancel</button>}
              </form>
            )}
            <div style={{overflowY:'auto',maxHeight:320}}>
              <table style={{width:'100%',borderCollapse:'collapse'}}>
                <thead>
                  <tr style={{background:'#f7f9fc',borderBottom:'1px solid #e8ecf3'}}>
                    <th style={{textAlign:'left',padding:'6px 8px',fontWeight:600}}>Route Name</th>
                    <th style={{textAlign:'left',padding:'6px 8px',fontWeight:600}}>Route ID</th>
                    {canEdit && <th style={{padding:'6px 8px'}}></th>}
                  </tr>
                </thead>
                <tbody>
                  {routes.map(route => (
                    <tr key={route.id} style={{borderBottom:'1px solid #eef2f6'}}>
                      <td style={{padding:'6px 8px'}}>{route.name}</td>
                      <td style={{padding:'6px 8px',color:'#64748b',fontSize:13}}>{route.routeId || route.id}</td>
                      {canEdit && <td style={{padding:'6px 8px'}}>
                        <button className="btn secondary" style={{fontSize:13,marginRight:4}} onClick={()=>startEdit(route)} disabled={loading}>Edit</button>
                        <button className="btn secondary" style={{fontSize:13,marginRight:4}} onClick={()=>handleDelete(route.id)} disabled={loading}>Delete</button>
                        <button className="btn primary" style={{fontSize:13}} onClick={()=>setVendorModalRoute(route)} disabled={loading}>Map Vendor</button>
                      </td>}
                    </tr>
                  ))}
                  {routes.length === 0 && <tr><td colSpan={canEdit ? 3 : 2} style={{color:'#888',padding:'6px 8px'}}>No routes yet.</td></tr>}
                </tbody>
              </table>
            </div>
            {/* Vendor Mapping Modal for selected route */}
            {vendorModalRoute && (
              <div style={{position:'fixed',top:0,left:0,width:'100vw',height:'100vh',background:'rgba(16,23,42,0.32)',zIndex:1100,display:'flex',alignItems:'center',justifyContent:'center'}}>
                <div style={{background:'#fff',borderRadius:18,padding:'36px 40px 32px 40px',minWidth:420,maxWidth:520,boxShadow:'0 8px 32px 0 rgba(15,23,42,0.18)',position:'relative',display:'flex',flexDirection:'column',alignItems:'center'}}>
                  <button onClick={()=>setVendorModalRoute(null)} style={{position:'absolute',top:18,right:18,fontSize:28,background:'#f7f9fc',border:'none',borderRadius:'50%',width:36,height:36,display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',color:'#2563eb',boxShadow:'0 2px 8px #2563eb11'}} aria-label="Close">×</button>
                  <div style={{fontWeight:900,fontSize:24,marginBottom:10,letterSpacing:0.2,color:'#1d2a3b',textAlign:'center'}}>Vendor Mapping</div>
                  <div style={{fontWeight:600,fontSize:17,marginBottom:22,color:'#2563eb',textAlign:'center'}}>Route: <span style={{fontWeight:700}}>{vendorModalRoute.name}</span></div>
                  <div style={{width:'100%'}}>
                    <VendorMapping
                      company={company}
                      canEdit={canEdit}
                      initialRouteId={vendorModalRoute.id}
                      routeOptions={[vendorModalRoute]}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
